import { trpc } from '@/trpcClient';
import { Card, CardContent } from '@shelflix/ui-web/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shelflix/ui-web/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@shelflix/ui-web/components/ui/tooltip';
import { TablePagination } from '@shelflix/ui-web/components/TablePagination';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Drawer } from '@/components/ui/drawer';
import Icon from '@shelflix/ui-web/components/Icon';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useState } from 'react';
import type { inferProcedureOutput } from '@trpc/server';
import type { Router } from '@shelflix/trpc';

type NotificationLog = inferProcedureOutput<Router['whatsappNotifications']['listLogs']>['logs'][number];

type RecipientDetail = { name: string; phone: string };

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  processing: 'Processando',
  completed: 'Concluído',
  failed: 'Falhou',
};

/**
 * Formats recipient details as bullet points for display in mobile drawer
 */
function formatRecipientsList(recipientDetails: RecipientDetail[] | undefined): string {
  if (!recipientDetails || recipientDetails.length === 0) {
    return '—';
  }
  // Remove duplicates based on phone number
  const uniqueRecipients = Array.from(new Map(recipientDetails.map((r) => [r.phone, r])).values());
  return uniqueRecipients.map((r) => `• ${r.name} (${r.phone})`).join('\n');
}

/**
 * Component to display recipients count with tooltip showing details
 */
function RecipientsTooltip({
  recipientCount,
  recipientDetails,
}: {
  recipientCount: number;
  recipientDetails: RecipientDetail[] | undefined;
}) {
  // Remove duplicates based on phone number
  const uniqueRecipients = recipientDetails
    ? Array.from(new Map(recipientDetails.map((r) => [r.phone, r])).values())
    : [];

  // If no details available, just show the count without tooltip
  if (uniqueRecipients.length === 0) {
    return <span>{recipientCount}</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="cursor-help underline decoration-dotted underline-offset-2 hover:text-[var(--primary)]"
        >
          {recipientCount}
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-h-[300px] max-w-[350px] overflow-y-auto bg-white text-left text-black dark:bg-gray-800 dark:text-white"
      >
        <div className="flex flex-col gap-1 p-1">
          <div className="mb-1 text-xs font-semibold">Destinatários:</div>
          {uniqueRecipients.map((recipient, idx) => (
            <div key={idx} className="text-xs">
              • {recipient.name} ({recipient.phone})
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export function LogsTab() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [notificationType, setNotificationType] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [showFilters, setShowFilters] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<NotificationLog | null>(null);

  const { data, isLoading, isError } = useQuery({
    ...trpc.whatsappNotifications.listLogs.queryOptions({
      page,
      pageSize,
      notificationType,
      status,
      dateFrom: dateFrom ? new Date(dateFrom).toISOString() : undefined,
      dateTo: dateTo ? new Date(dateTo).toISOString() : undefined,
    }),
    refetchInterval: 30000,
  });

  const hasActiveFilters = dateFrom || dateTo || notificationType || status;

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setNotificationType(undefined);
    setStatus(undefined);
    setPage(1);
  };

  const formatDateTime = (dateString: string) => {
    try {
      // Parse the string as UTC if it doesn't have timezone info, then format in local timezone
      const utcDateString = dateString.includes('Z') || dateString.includes('+') ? dateString : dateString + 'Z';
      const date = new Date(utcDateString);
      return format(date, "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const handleOpenDrawer = (log: NotificationLog) => {
    setSelectedLog(log);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedLog(null);
  };

  if (isError) {
    return (
      <Card className="p-6">
        <p className="text-destructive text-sm">Erro ao carregar logs de notificações</p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-4 md:px-0">
      {/* Filter Toggle */}
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-2">
          <Icon name="filter_alt" size={16} />
          <span>Filtros</span>
        </Button>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-2 text-xs">
            <Icon name="close" size={14} />
            <span>Limpar filtros</span>
          </Button>
        )}
      </div>

      {/* Filters Card */}
      {showFilters && (
        <Card className="rounded-[10px] border-none px-4 shadow-none md:px-8">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="flex flex-col gap-2">
                <label htmlFor="dateFrom" className="text-xs font-medium text-[var(--gray-primary)]">
                  Data início
                </label>
                <input
                  id="dateFrom"
                  type="datetime-local"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setPage(1);
                  }}
                  className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="dateTo" className="text-xs font-medium text-[var(--gray-primary)]">
                  Data fim
                </label>
                <input
                  id="dateTo"
                  type="datetime-local"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setPage(1);
                  }}
                  className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="notificationType" className="text-xs font-medium text-[var(--gray-primary)]">
                  Tipo de notificação
                </label>
                <Select
                  value={notificationType || 'all'}
                  onValueChange={(value) => {
                    setNotificationType(value === 'all' ? undefined : value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="notificationType" className="h-10 w-full">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="material_transfer">Transferência de Material</SelectItem>
                    <SelectItem value="campaign_expiring">Campanha Expirando</SelectItem>
                    <SelectItem value="material_offline_30min">Material Offline (30min)</SelectItem>
                    <SelectItem value="material_offline_60min">Material Offline (60min)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="status" className="text-xs font-medium text-[var(--gray-primary)]">
                  Status
                </label>
                <Select
                  value={status || 'all'}
                  onValueChange={(value) => {
                    setStatus(value === 'all' ? undefined : value);
                    setPage(1);
                  }}
                >
                  <SelectTrigger id="status" className="h-10 w-full">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {Object.entries(STATUS_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Desktop Table */}
      <Card className="hidden w-full overflow-hidden rounded-[10px] md:block">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-neutral-600 bg-[var(--secondary)]">
              <TableHead className="!h-10 w-[10%] overflow-hidden border-r border-neutral-600 text-ellipsis">
                Data
              </TableHead>
              <TableHead className="!h-10 w-[18%] overflow-hidden border-r border-neutral-600 text-ellipsis">
                Tipo
              </TableHead>
              <TableHead className="!h-10 w-[15%] overflow-hidden border-r border-neutral-600 text-ellipsis">
                Status
              </TableHead>
              <TableHead className="!h-10 w-[10%] overflow-hidden border-r border-neutral-600 text-ellipsis">
                Destinatários
              </TableHead>
              <TableHead className="!h-10 w-[39%] overflow-hidden text-ellipsis">Mensagem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                    <span className="text-muted-foreground text-sm">Carregando...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : !data?.logs.length ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center">
                  <span className="text-muted-foreground text-sm">
                    {hasActiveFilters ? 'Nenhum log encontrado com os filtros aplicados' : 'Nenhum log de notificação'}
                  </span>
                </TableCell>
              </TableRow>
            ) : (
              data.logs.map((log, index) => (
                <TableRow key={log.id} className={index % 2 === 1 ? 'bg-[var(--secondary)]' : ''}>
                  <TableCell className="!h-10 w-[18%] border-r border-neutral-600 text-xs font-medium">
                    <div className="overflow-hidden break-words text-ellipsis">{formatDateTime(log.createdAt)}</div>
                  </TableCell>
                  <TableCell
                    className="!h-10 w-[18%] border-r border-neutral-600 font-mono text-xs"
                    title={log.notificationType}
                  >
                    <div className="overflow-hidden break-words text-ellipsis">{log.notificationType}</div>
                  </TableCell>
                  <TableCell className="!h-10 w-[15%] border-r border-neutral-600">
                    <div className="flex items-center justify-center">
                      <span
                        className={`rounded px-2 py-1 text-xs font-medium ${
                          log.status === 'completed'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : log.status === 'failed'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              : log.status === 'processing'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                        }`}
                      >
                        {STATUS_LABELS[log.status] || log.status}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="!h-10 w-[10%] border-r border-neutral-600 text-center text-xs">
                    <RecipientsTooltip
                      recipientCount={log.recipientCount}
                      recipientDetails={log.recipientDetails as RecipientDetail[] | undefined}
                    />
                  </TableCell>
                  <TableCell className="!h-10 w-[39%] text-xs" title={log.message}>
                    <div className="overflow-hidden break-words whitespace-normal">{log.message}</div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Mobile Table */}
      <Card className="w-full overflow-hidden rounded-[10px] md:hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-neutral-600 bg-[var(--secondary)]">
              <TableHead className="!h-10 w-[35%] overflow-hidden border-r border-neutral-600 text-ellipsis">
                Data
              </TableHead>
              <TableHead className="!h-10 w-[45%] overflow-hidden border-r border-neutral-600 text-ellipsis">
                Tipo
              </TableHead>
              <TableHead className="!h-10 w-[20%] overflow-hidden text-ellipsis">Mais</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} className="h-24 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                    <span className="text-muted-foreground text-sm">Carregando...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : !data?.logs.length ? (
              <TableRow>
                <TableCell colSpan={3} className="h-24 text-center">
                  <span className="text-muted-foreground text-sm">
                    {hasActiveFilters ? 'Nenhum log encontrado com os filtros aplicados' : 'Nenhum log de notificação'}
                  </span>
                </TableCell>
              </TableRow>
            ) : (
              data.logs.map((log, index) => (
                <TableRow key={log.id} className={index % 2 === 1 ? 'bg-[var(--secondary)]' : ''}>
                  {/* Date */}
                  <TableCell className="!h-10 w-[35%] border-r border-neutral-600">
                    <div className="text-xs text-gray-700 dark:text-gray-300">
                      {format(
                        new Date(
                          log.createdAt.includes('Z') || log.createdAt.includes('+')
                            ? log.createdAt
                            : log.createdAt + 'Z'
                        ),
                        'dd/MM/yy HH:mm',
                        { locale: ptBR }
                      )}
                    </div>
                  </TableCell>

                  {/* Type */}
                  <TableCell className="!h-10 w-[45%] border-r border-neutral-600" title={log.notificationType}>
                    <div className="overflow-hidden font-mono text-xs break-words text-ellipsis">
                      {log.notificationType}
                    </div>
                  </TableCell>

                  {/* Details - Button to open drawer */}
                  <TableCell className="!h-10 w-[20%]">
                    <div className="flex items-center justify-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="btn-theme-ghost h-8 w-8"
                        onClick={() => handleOpenDrawer(log)}
                      >
                        <Icon name="clarify" variant="outlined" size={20} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex flex-col items-center gap-4">
          <TablePagination page={page} totalItems={data.total} itemsPerPage={pageSize} onPageChange={setPage} />
          <p className="text-muted-foreground text-sm">
            Mostrando {(page - 1) * pageSize + 1} a {Math.min(page * pageSize, data.total)} de {data.total} registros
          </p>
        </div>
      )}

      {/* Mobile Details Drawer */}
      <Drawer open={drawerOpen} onOpenChange={handleCloseDrawer}>
        {selectedLog && (
          <div className="flex flex-col gap-6 p-4">
            <div>
              <h3 className="mb-6 text-lg font-semibold">Detalhes do Log</h3>

              <div className="space-y-6">
                {/* Data */}
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">Data</label>
                  <div className="rounded-lg border border-neutral-300 bg-[var(--secondary-light)] p-3 break-words dark:border-neutral-600 dark:bg-[var(--secondary-dark)]">
                    <p className="text-sm break-words">{formatDateTime(selectedLog.createdAt)}</p>
                  </div>
                </div>

                {/* Tipo de Notificação */}
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    Tipo de Notificação
                  </label>
                  <div className="rounded-lg border border-neutral-300 bg-[var(--secondary-light)] p-3 break-words dark:border-neutral-600 dark:bg-[var(--secondary-dark)]">
                    <p className="font-mono text-sm break-words">{selectedLog.notificationType}</p>
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">Status</label>
                  <div className="rounded-lg border border-neutral-300 bg-[var(--secondary-light)] p-3 dark:border-neutral-600 dark:bg-[var(--secondary-dark)]">
                    <span
                      className={`inline-block rounded px-2 py-1 text-xs font-medium ${
                        selectedLog.status === 'completed'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : selectedLog.status === 'failed'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : selectedLog.status === 'processing'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                      }`}
                    >
                      {STATUS_LABELS[selectedLog.status] || selectedLog.status}
                    </span>
                  </div>
                </div>

                {/* Destinatários */}
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">
                    Destinatários ({selectedLog.recipientCount})
                  </label>
                  <div className="rounded-lg border border-neutral-300 bg-[var(--secondary-light)] p-3 dark:border-neutral-600 dark:bg-[var(--secondary-dark)]">
                    <p className="text-sm whitespace-pre-line">
                      {formatRecipientsList(selectedLog.recipientDetails as RecipientDetail[] | undefined)}
                    </p>
                  </div>
                </div>

                {/* Mensagem */}
                <div>
                  <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">Mensagem</label>
                  <div className="rounded-lg border border-neutral-300 bg-[var(--secondary-light)] p-3 break-words dark:border-neutral-600 dark:bg-[var(--secondary-dark)]">
                    <p className="text-sm break-words whitespace-pre-wrap">{selectedLog.message}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
